import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Users } from 'lucide-react';
import { BRAND_FULL_NAME, THEME } from '../../config/brand';
import { useAuth } from '../../contexts/AuthContext';
import ScrollReveal from '../common/ScrollReveal';

const { navy, gold, goldDark, cream, creamBorder, muted } = THEME;

/** Cream CTA band — same language as hero CTAs */
const HomeCtaBanner: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const inner = (
    <ScrollReveal variant="up">
      <div
        className="relative mx-auto max-w-[92rem] overflow-hidden px-2 sm:px-3 lg:px-5"
      >
        <div
          className="relative overflow-hidden rounded-[1.5rem] border px-5 py-12 text-center sm:rounded-[2rem] sm:px-8 sm:py-14 lg:rounded-[2.25rem] lg:px-12 lg:py-16"
          style={{
            background: cream,
            borderColor: creamBorder,
          }}
        >
          <div
            className="pointer-events-none absolute inset-0 opacity-50"
            aria-hidden
            style={{
              backgroundImage:
                'linear-gradient(rgba(0,24,72,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(0,24,72,0.04) 1px, transparent 1px)',
              backgroundSize: '28px 28px',
            }}
          />
          <div
            className="pointer-events-none absolute inset-0"
            aria-hidden
            style={{
              background:
                'radial-gradient(ellipse 55% 60% at 80% 20%, rgba(192,144,72,0.14), transparent 55%), radial-gradient(ellipse 40% 50% at 10% 80%, rgba(0,24,72,0.05), transparent 50%)',
            }}
          />

          <div className="relative mx-auto max-w-3xl">
            <p className="mb-3 text-sm font-semibold" style={{ color: goldDark }}>
              ابدأ دلوقتي
            </p>
            <h3
              className="mb-3 text-xl font-bold leading-snug font-cairo-bold sm:text-2xl lg:text-3xl"
              style={{ color: navy }}
            >
              مستعد تبدأ في {BRAND_FULL_NAME}؟
            </h3>
            <p
              className="mx-auto mb-7 max-w-lg text-sm leading-relaxed sm:mb-8 sm:text-base"
              style={{ color: muted }}
            >
              انضم دلوقتي، اختار مدرسك، وابدأ تتعلم بالطريقة اللي تناسبك.
            </p>

            <div className="flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:items-center">
              {mounted && (
                <button
                  type="button"
                  onClick={() =>
                    navigate(isAuthenticated ? '/student-portal' : '/register')
                  }
                  className="inline-flex min-h-[50px] w-full items-center justify-center gap-2 rounded-full px-7 text-sm font-cairo-bold text-white transition hover:brightness-105 sm:w-auto sm:min-h-[54px] sm:px-8 sm:text-base"
                  style={{ background: gold, boxShadow: '0 10px 28px rgba(192,144,72,0.35)' }}
                >
                  {isAuthenticated ? 'ادخل لمنصتك' : 'سجّل مجاناً'}
                  <ArrowLeft className="h-4 w-4" aria-hidden />
                </button>
              )}
              <button
                type="button"
                onClick={() =>
                  document.getElementById('teachers-section')?.scrollIntoView({ behavior: 'smooth' })
                }
                className="inline-flex min-h-[50px] w-full items-center justify-center gap-2 rounded-full border-2 bg-white/70 px-6 text-sm font-semibold backdrop-blur-sm transition hover:bg-white sm:w-auto sm:min-h-[54px] sm:px-7 sm:text-base"
                style={{ borderColor: navy, color: navy }}
              >
                <Users className="h-4 w-4" aria-hidden />
                شوف المدرسين
              </button>
            </div>
          </div>
        </div>
      </div>
    </ScrollReveal>
  );

  if (embedded) {
    return <div id="home-cta-section">{inner}</div>;
  }

  return (
    <section id="home-cta-section" className="relative py-5 sm:py-6 lg:py-8">
      {inner}
    </section>
  );
};

export default HomeCtaBanner;
